import { useQueries } from '@tanstack/react-query';
import { client } from '../api/generated/client.gen';
import { useErrorStore } from '../stores/ErrorStore';
import { queryKeys } from './queryKeys';

interface UseFetchImagesOptions {
  orchestrationId: string;
  imagesToLoad: number[];
  imageFilenames: string[];
}

/**
 * Hook to fetch multiple images with React Query caching
 * Uses useQueries to dynamically create queries based on indices array
 */
export const useFetchImages = ({
  orchestrationId,
  imagesToLoad,
  imageFilenames,
}: UseFetchImagesOptions) => {
  const { addError } = useErrorStore();

  return useQueries({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queries: imagesToLoad.map((index) => ({
      queryKey: queryKeys.orchestrationImage(
        orchestrationId,
        imageFilenames[index],
      ),
      queryFn: async () => {
        const filename = imageFilenames[index];
        try {
          const response = await client.instance.get(
            `/api/v1/delft3d-orchestrations/${orchestrationId}/results/download`,
            {
              params: { file: filename },
              responseType: 'blob',
            },
          );

          if (response.data) {
            return new Blob([response.data]);
          }
          return null;
        } catch (error) {
          addError(`Failed to load image: ${filename}`);
          return null;
        }
      },
      enabled: !!orchestrationId && !!imageFilenames[index],
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000,
      retry: 2,
    })),
  });
};
